import { useEffect, useRef } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './auth/AuthProvider';
import { setTokenProvider } from './services/api';
import { ThemeProvider } from './providers/ThemeProvider';
import { BreadcrumbProvider } from './providers/BreadcrumbProvider';
import { TooltipProvider } from './components/ui/tooltip';
import { EmailHealthProvider } from './providers/EmailHealthProvider';
import EmailHealthFloatingProgress from './components/EmailHealth/EmailHealthFloatingProgress';
import CommandPalette from './components/CommandPalette/CommandPalette';
import Layout from './components/Layout/Layout';
import DashboardPage from './components/Dashboard/DashboardPage';
import ClientsPage from './components/Clients/ClientsPage';
import ClientDetail from './components/Clients/ClientDetail';
import OnboardingWizard from './components/Onboarding/OnboardingWizard';
import SubscriptionsPage from './components/Subscriptions/SubscriptionsPage';
import SubscriptionDetail from './components/Subscriptions/SubscriptionDetail';
import AlertsPage from './components/Alerts/AlertsPage';
import SecurityDashboard from './components/Security/SecurityDashboard';
import SecurityAlertsPage from './components/Security/SecurityAlertsPage';
import ThreatMap from './components/Security/ThreatMap';
import InvestigationsPage from './components/Security/InvestigationsPage';
import InvestigationDetail from './components/Security/InvestigationDetail';
import ReportsPage from './components/Reports/ReportsPage';
import AuditLogsPage from './components/AuditLogs/AuditLogsPage';
import ChangelogPage from './components/Changelog/ChangelogPage';
import HelpPage from './components/Help/HelpPage';
import ScheduledReportsPage from './components/ScheduledReports/ScheduledReportsPage';
import ConsentCallback from './components/ConsentCallback';
import EmailTest from './components/DevTools/EmailTest';
import SettingsPage from './components/Settings/SettingsPage';
import ViewCredentials from './components/Credentials/ViewCredentials';
import UploadSpamSamples from './components/SpamUpload/UploadSpamSamples';
import TenantMatcherPage from './components/TenantMatcher/TenantMatcherPage';
import EmbeddedDashboard from './components/Embedded/EmbeddedDashboard';
import RenewalCalendar from './components/Calendar/RenewalCalendar';
import FloatingDebugPanel from './components/DebugPanel/FloatingDebugPanel';
import MessageTracePage from './components/MessageTrace/MessageTracePage';
import EmailHealthDashboard from './components/EmailHealth/EmailHealthDashboard';
import DkimMigrationPage from './components/EmailHealth/DkimMigrationPage';
import CompliancePage from './components/Compliance/CompliancePage';
import CompliancePortfolioPage from './components/Compliance/CompliancePortfolioPage';
import MarginAuditPage from './components/MarginAudit/MarginAuditPage';
import { Skeleton } from './components/ui/skeleton';

/**
 * Detect if running in embed mode (iframe from HUD)
 * Security: CSP frame-ancestors restricts who can embed, backend validates Origin
 */
function isEmbedMode(): boolean {
  if (window.location.pathname === '/embedded') return true;
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}

// Loading skeleton component
function LoadingScreen({ message = 'Authenticating...' }: { message?: string }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center space-y-4">
        <div className="flex items-center justify-center">
          <div className="flex items-center justify-center w-12 h-12 rounded-xl gradient-primary shadow-glow-sm">
            <span className="text-white font-bold text-lg">P8</span>
          </div>
        </div>
        <div className="space-y-2">
          <Skeleton className="h-4 w-48 mx-auto" />
          <Skeleton className="h-3 w-32 mx-auto" />
        </div>
        <p className="text-sm text-muted-foreground">{message}</p>
      </div>
    </div>
  );
}

// Component to handle RingCentral OAuth redirect
function RingCentralRedirectHandler() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const rcError = params.get('rc_error');
    const rcSuccess = params.get('rc_success');
    if ((rcError || rcSuccess) && !location.pathname.startsWith('/settings')) {
      navigate(`/settings/user${location.search}`, { replace: true });
    }
  }, [location, navigate]);

  return null;
}

function AppRoutes({ embedMode }: { embedMode: boolean }) {
  if (embedMode) {
    return (
      <Routes>
        <Route path="/credentials/:token" element={<ViewCredentials />} />
        <Route path="/upload/:token" element={<UploadSpamSamples />} />
        <Route path="/" element={<Layout />}>
          <Route index element={<DashboardPage />} />
          <Route path="clients" element={<ClientsPage />} />
          <Route path="clients/onboard" element={<OnboardingWizard />} />
          <Route path="clients/:id" element={<ClientDetail />} />
          <Route path="subscriptions" element={<SubscriptionsPage />} />
          <Route path="subscriptions/:id" element={<SubscriptionDetail />} />
          <Route path="calendar" element={<RenewalCalendar />} />
          <Route path="margin-audit" element={<MarginAuditPage />} />
          <Route path="alerts" element={<AlertsPage />} />
          <Route path="reports" element={<ReportsPage />} />
          <Route path="scheduled-reports" element={<ScheduledReportsPage />} />
          <Route path="audit-logs" element={<AuditLogsPage />} />
          <Route path="message-trace" element={<MessageTracePage />} />
          <Route path="email-health" element={<EmailHealthDashboard />} />
          <Route path="email-health/dkim-migration" element={<DkimMigrationPage />} />
          <Route path="security" element={<SecurityDashboard />} />
          <Route path="security/alerts" element={<SecurityAlertsPage />} />
          <Route path="security/map" element={<ThreatMap />} />
          <Route path="security/investigations" element={<InvestigationsPage />} />
          <Route path="compliance" element={<CompliancePortfolioPage />} />
          <Route path="compliance/:clientId" element={<CompliancePage />} />
          <Route path="security/investigations/:id" element={<InvestigationDetail />} />
          <Route path="changelog" element={<ChangelogPage />} />
          <Route path="help" element={<HelpPage />} />
          <Route path="settings" element={<Navigate to="/settings/user" replace />} />
          <Route path="settings/user" element={<SettingsPage tab="user" />} />
          <Route path="settings/system" element={<SettingsPage tab="system" />} />
          <Route path="settings/security" element={<SettingsPage tab="security" />} />
          <Route path="tenant-matcher" element={<TenantMatcherPage />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/consent-callback" element={<ConsentCallback />} />
      <Route path="/credentials/:token" element={<ViewCredentials />} />
      <Route path="/upload/:token" element={<UploadSpamSamples />} />
      <Route path="/embedded" element={<EmbeddedDashboard />} />
      <Route
        path="/"
        element={
          <>
            <RingCentralRedirectHandler />
            <Layout />
          </>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="clients" element={<ClientsPage />} />
        <Route path="clients/onboard" element={<OnboardingWizard />} />
        <Route path="clients/:id" element={<ClientDetail />} />
        <Route path="subscriptions" element={<SubscriptionsPage />} />
        <Route path="subscriptions/:id" element={<SubscriptionDetail />} />
        <Route path="calendar" element={<RenewalCalendar />} />
        <Route path="margin-audit" element={<MarginAuditPage />} />
        <Route path="alerts" element={<AlertsPage />} />
        <Route path="reports" element={<ReportsPage />} />
        <Route path="scheduled-reports" element={<ScheduledReportsPage />} />
        <Route path="audit-logs" element={<AuditLogsPage />} />
        <Route path="message-trace" element={<MessageTracePage />} />
        <Route path="email-health" element={<EmailHealthDashboard />} />
        <Route path="email-health/dkim-migration" element={<DkimMigrationPage />} />
        <Route path="security" element={<SecurityDashboard />} />
        <Route path="security/alerts" element={<SecurityAlertsPage />} />
        <Route path="security/map" element={<ThreatMap />} />
        <Route path="security/investigations" element={<InvestigationsPage />} />
        <Route path="security/investigations/:id" element={<InvestigationDetail />} />
        <Route path="changelog" element={<ChangelogPage />} />
        <Route path="help" element={<HelpPage />} />
        <Route path="dev/email-test" element={<EmailTest />} />
        <Route path="settings" element={<Navigate to="/settings/user" replace />} />
        <Route path="settings/user" element={<SettingsPage tab="user" />} />
        <Route path="settings/system" element={<SettingsPage tab="system" />} />
        <Route path="settings/security" element={<SettingsPage tab="security" />} />
        <Route path="tenant-matcher" element={<TenantMatcherPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  const { getAccessToken, isAuthenticated, isLoading, login } = useAuth();
  const embedMode = isEmbedMode();
  const loginInitiated = useRef(false);

  useEffect(() => {
    if (isAuthenticated) {
      setTokenProvider(getAccessToken);
    }
  }, [isAuthenticated, getAccessToken]);

  useEffect(() => {
    if (embedMode) return;
    if (!isAuthenticated && !isLoading && !loginInitiated.current) {
      loginInitiated.current = true;
      login();
    }
    if (isAuthenticated) {
      loginInitiated.current = false;
    }
  }, [isAuthenticated, isLoading, login, embedMode]);

  if (!embedMode && (isLoading || !isAuthenticated)) {
    return (
      <ThemeProvider>
        <LoadingScreen message="Authenticating..." />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider>
      <TooltipProvider>
        <BreadcrumbProvider>
          <EmailHealthProvider>
            <AppRoutes embedMode={embedMode} />
            <CommandPalette />
            <FloatingDebugPanel />
            <EmailHealthFloatingProgress />
          </EmailHealthProvider>
        </BreadcrumbProvider>
      </TooltipProvider>
    </ThemeProvider>
  );
}
